import React from 'react';
import {
  Box, Button, HStack, Text, useToast,
} from '@chakra-ui/react';
import IconProposals from '../assets/Icon-Proposals.svg';
import DocumentGridShell from './DocumentGridShell';
import LedgerTable, { LedgerColumn } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import useProposalsArchived from '../hooks/useProposalsArchived';
import useCurrentUser from '../hooks/useCurrentUser';
import { Proposal } from '../models/proposal';
import { money } from '../utils/money';
import { SqlDateTimeToDisplay } from '../utils/SqlTools'; // item 1331 — CREATED column

const amountOf = (p: Proposal): number => (p as any).retailProposalTotal ?? (p as any).retailTotal ?? 0;

// item 485/553 — ARCHIVED proposals: archive punts a proposal out of the main ledger into
// its own area (void stays in the list, badged). item 553 moved this off a bare table onto the
// shared LEDGER grammar (collapsible DocumentGridShell + LedgerTable) so it matches every other
// project section; Restore takes the primary-action slot (blue), moderator-gated.
const ArchivedProposalsSection: React.FC<{
  projectId?: string;
  hideWhenEmpty?: boolean;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, hideWhenEmpty, collapse }) => {
  const {
    archived, isLoadingArchived, restore, isRestoring,
  } = useProposalsArchived();
  const { canModerate } = useCurrentUser();
  const toast = useToast();

  const rows = (archived as Proposal[]).filter((p) => !projectId || p.projectId === projectId);

  // item 553 — only surface the section when there ARE archived proposals; an empty Archived
  // section would just clutter the project (Chad).
  if (hideWhenEmpty && !isLoadingArchived && rows.length === 0) return null;

  const doRestore = async (p: Proposal) => {
    try {
      await restore(p.id);
      toast({ status: 'success', title: `Restored ${p.proposalNumber || p.proposalTitle}.` });
    } catch (e: any) {
      toast({
        status: 'error',
        title: 'Restore failed',
        description: e?.response?.status === 403 ? 'Only an administrator can restore.' : 'Try again.',
      });
    }
  };

  const columns: LedgerColumn<Proposal>[] = [
    {
      // item 1445 (Chad, after the 1444 invoice fold: "I thought we were doing a pass on all the ledgers") —
      // PROPOSAL = number bold, title as two-line subtext, customer beneath (mirrors ProposalsGrid).
      // item 1447 — title + customer ride the full-width rowSubline; number here.
      key: 'number',
      header: 'PROPOSAL',
      width: '150px',
      sortValue: (p) => p.proposalNumber ?? '',
      render: (p) => <Text fontWeight="semibold" whiteSpace="nowrap">{p.proposalNumber ?? '—'}</Text>,
    },
    {
      // item 1331 (Chad: "do we have room to add a date") — CREATED date; the ledger's default sort
      // (newest on top) so the chevron has a column to sit on. Number order lies for revisions.
      key: 'created',
      header: 'CREATED',
      width: '110px',
      sortValue: (p) => (p as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (p as { lastModifiedDate?: string }).lastModifiedDate ?? '',
      render: (p) => {
        const d = (p as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (p as { lastModifiedDate?: string }).lastModifiedDate;
        return <span style={{ whiteSpace: 'nowrap' }}>{d ? SqlDateTimeToDisplay(d) : '—'}</span>;
      },
    },
    // NAME (title + customer) folded under the number — item 1445.
    {
      key: 'amount', header: 'AMOUNT', width: '120px', align: 'right', sortValue: (p) => amountOf(p), render: (p) => money(amountOf(p)),
    },
  ];

  const rowActions = (p: Proposal): React.ReactNode => (
    <HStack spacing={1} justify="flex-end">
      {canModerate && (
        <Button size="xs" minW="76px" colorScheme="blue" isLoading={isRestoring} onClick={() => doRestore(p)}>
          Restore
        </Button>
      )}
    </HStack>
  );

  return (
    <DocumentGridShell
      heading="ARCHIVED PROPOSALS"
      icon={IconProposals}
      collapsible={!!collapse}
      collapsed={collapse?.collapsed}
      onToggleCollapse={collapse?.onToggle}
      status={isLoadingArchived ? 'loading' : 'success'}
      data={rows}
    >
      <LedgerTable<Proposal>
        defaultSort={{ key: 'created', dir: -1 }} // item 1331 (Chad) — DATE order, newest on top (number order lies for revisions)
        rows={rows}
        columns={columns}
        rowSubline={(p) => <LedgerSubline name={p.proposalTitle} meta={[p.customerName]} />}
        rowId={(p) => p.id}
        rowActions={rowActions}
        rowActionsWidth="120px"
        rowActionsHeader="ACTION"
        selectable={false}
        searchText={(p) => `${p.proposalNumber ?? ''} ${p.proposalTitle ?? ''} ${p.customerName ?? ''}`}
        searchPlaceholder="Search archived proposals…"
        emptyText="No archived proposals."
      />
    </DocumentGridShell>
  );
};

ArchivedProposalsSection.defaultProps = { projectId: undefined, hideWhenEmpty: false, collapse: undefined };

export default ArchivedProposalsSection;
